import { useKestrelViewerContext } from 'features/kestrelViewer'
import { DockedPhotoReelPanel } from 'features/kestrelViewer/ui/tools/photoreel/DockedPhotoReelPanel'
import { PhotoReelCarousel } from 'features/kestrelViewer/ui/tools/photoreel/PhotoReelCarousel'
import React, { RefObject, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

const OuterContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`

const ViewerContainer = styled.div<{ $docked: boolean }>`
  flex: ${({ $docked }) => ($docked ? '1' : '1')};
  height: 100%;
  position: relative;
  min-width: 50%;
`

const DockedPanel = styled.div<{ $width: number; $isResizing: boolean }>`
  width: ${({ $width }) => $width}%;
  min-width: 30%;
  max-width: 50%;
  height: 100%;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  flex-shrink: 0;
  transition: ${({ $isResizing }) =>
    $isResizing ? 'none' : 'width 0.15s ease'};
`

const ResizeHandle = styled.div<{ $isResizing: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
  background: ${({ $isResizing }) =>
    $isResizing ? 'rgba(59, 130, 246, 0.5)' : 'transparent'};
  z-index: 20;

  &:hover {
    background: rgba(59, 130, 246, 0.3);
  }

  &::before {
    content: '';
    position: absolute;
    left: 2px;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: 40px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 1px;
  }
`

// Blocks pixel streaming interactions during resize
const ResizeOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  cursor: ew-resize;
`

interface KestrelViewerLayoutProps {
  children: React.ReactNode
  containerRef: RefObject<HTMLDivElement>
  videoParentRef: RefObject<HTMLDivElement>
}

/**
 * KestrelViewerLayout handles the layout for the Kestrel viewer, including
 * the docked photo reel panel and resize functionality.
 *
 * This component is rendered as a child of KestrelViewerProvider, so it can
 * access the viewer context via useKestrelViewerContext().
 */
export const KestrelViewerLayout: React.FC<KestrelViewerLayoutProps> = ({
  children,
  containerRef,
  videoParentRef,
}) => {
  const { photoReelDocked } = useKestrelViewerContext()

  const [dockedPanelWidth, setDockedPanelWidth] = useState(33.33) // percentage
  const [isResizing, setIsResizing] = useState(false)

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      // Calculate percentage from right edge of the window
      const containerWidth = window.innerWidth
      const newWidth = ((containerWidth - e.clientX) / containerWidth) * 100
      // Clamp between 30% (min) and 50% (max)
      setDockedPanelWidth(Math.min(Math.max(newWidth, 30), 50))
    }

    const handleMouseUp = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsResizing(false)
    }

    // Add cursor style to body during resize and block pointer events on viewer
    document.body.style.cursor = 'ew-resize'
    document.body.style.userSelect = 'none'

    document.addEventListener('mousemove', handleMouseMove, { capture: true })
    document.addEventListener('mouseup', handleMouseUp, { capture: true })

    return () => {
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', handleMouseMove, {
        capture: true,
      })
      document.removeEventListener('mouseup', handleMouseUp, { capture: true })
    }
  }, [isResizing])

  return (
    <OuterContainer>
      <ViewerContainer ref={containerRef} $docked={photoReelDocked}>
        {/* Overlay to block interactions during panel resize */}
        {isResizing && <ResizeOverlay />}
        {/* Video parent element - PixelStreaming will inject video here */}
        <div ref={videoParentRef}></div>
        {!photoReelDocked && <PhotoReelCarousel />}
        {children}
      </ViewerContainer>
      {photoReelDocked && (
        <DockedPanel $width={dockedPanelWidth} $isResizing={isResizing}>
          <ResizeHandle
            $isResizing={isResizing}
            onMouseDown={handleResizeMouseDown}
          />
          <DockedPhotoReelPanel />
        </DockedPanel>
      )}
    </OuterContainer>
  )
}
